import React, { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { EVENT_QUERY_STRINGS } from '../../../../constants/eventsFairs.constants';
import { iconSize } from '../../../../constants/icon.constants';
import { documentService } from '../../../../utils/window.utils';
import DictionaryTerm from '../../../@shared/dictionaryTerm';
import Accordion, { TAccordionProps } from '../../../@shared/filters/components/accordion';
import Icon from '../../../@shared/icon';
import { EventsListingModel } from '../../models/eventsListing.model';
import { EventListingFilters, TSelectedEventsFilters } from '../../types/eventsListing.types';
import { ctaTypes } from '../../../@shared/cta';
import { googleClickDropdownEvent } from '../../../../utils/analytics';

const MobileFilter: FC<{
  model: EventsListingModel;
  selectedFilters: TSelectedEventsFilters;
  typeFilterDropdownOptions: TAccordionProps['accordionOptions'];
  timelineFilterDropdownOptions: TAccordionProps['accordionOptions'];
}> = ({ model, selectedFilters, typeFilterDropdownOptions, timelineFilterDropdownOptions }) => {
  const [shouldOpenFilterModal, setShouldOpenFilterModal] = useState(false);
  const [selectedFiltersCopy, setSelectedFiltersCopy] = useState(selectedFilters);
  const [selectedAccordionId, setSelectedAccordionId] = useState<string | undefined>();
  const navigate = useNavigate();

  useEffect(() => {
    const docElement = documentService();
    if (!docElement?.body) return;

    if (shouldOpenFilterModal) docElement.body.classList.add('body--with-filter-model');
    else docElement.body.classList.remove('body--with-filter-model');
  }, [shouldOpenFilterModal]);

  const { EVENT_TYPE, EVENT_TIMELINE } = EVENT_QUERY_STRINGS;
  // show the action CTA's if any option is selectd or change in selected option
  const shouldShowFilterWrapper =
    !(
      selectedFiltersCopy[EVENT_TIMELINE] === selectedFilters[EVENT_TIMELINE] &&
      selectedFiltersCopy[EVENT_TYPE] === selectedFilters[EVENT_TYPE]
    ) ||
    selectedFiltersCopy[EVENT_TIMELINE] !== '' ||
    selectedFiltersCopy[EVENT_TYPE] !== '';

  const accordionsProps = [
    {      
      canShowFilter: model.canShowFilter(EventListingFilters.TIMELINE),
      options: timelineFilterDropdownOptions,
      queryKey: EVENT_TIMELINE,
      id: 'timeline-accordion',
      accordionTitle: <DictionaryTerm keyword="cnhi-events-timeline-filter-title" />,
    },
    {
      canShowFilter: model.canShowFilter(EventListingFilters.TYPE),
      options: typeFilterDropdownOptions,
      queryKey: EVENT_TYPE,
      id: 'type-accordion',
      accordionTitle: <DictionaryTerm keyword="cnhi-events-type-filter-title" />,
    },
  ];

  const revertToOldFilters = () => model.updateQueryByBulkKeys(navigate, selectedFiltersCopy);
  const toggleModal = () => {
    if (!shouldOpenFilterModal) setSelectedAccordionId(undefined);
    setShouldOpenFilterModal(!shouldOpenFilterModal);
  };

  const toggleAccordion = (accordionId: typeof selectedAccordionId) => {
    if (accordionId === selectedAccordionId) setSelectedAccordionId(undefined);
    else setSelectedAccordionId(accordionId);
  };

  return (
    <div className="events-listing-with-filter--mbl">
      <div
        className="events-filter-modal-switch"
        onClick={() => {
          toggleModal();
          revertToOldFilters();
        }}
      >
        <DictionaryTerm keyword="cnhi-events-filter-title" />
        <Icon iconName="set-nav-services-funnel" size={iconSize['dsk']} />
      </div>
      {shouldOpenFilterModal && (
        <div className="events-filter-modal">
          <div className="events-filter-modal__header">
            <span className="events-filter-modal__title">
              <DictionaryTerm keyword="cnhi-events-filter-title" />
            </span>
            <Icon
              iconName="action-close"
              color="white"
              size={iconSize['dsk']}
              onClick={() => {
                setShouldOpenFilterModal(false);
                revertToOldFilters();
              }}
            />
          </div>
          <div className="events-filter-modal__body">
            {accordionsProps?.map(({ id, accordionTitle, queryKey, options, canShowFilter }) => {
              if (!options?.length || !canShowFilter) return null;
              return (
                <Accordion
                  key={id}
                  accordionTitle={accordionTitle}
                  accordionOptions={options}
                  isToggleIndependent={false}
                  canShowAccordionContent={selectedAccordionId === id}
                  handleSwitchClick={() => toggleAccordion(id)}
                  handleOptionClick={(ind) => {
                    model.updateQueryByKey(navigate, queryKey, ind === 0 ? '' : (options?.[ind].id as string));
                    googleClickDropdownEvent(id, options[ind].title as string);
                  }}
                  selectedOptionInd={
                    selectedFilters[queryKey] ? options?.findIndex((_) => _.id?.toString() === selectedFilters[queryKey]) : 0
                  }
                />
              );
            })}
          </div>
          {shouldShowFilterWrapper && (
            <div className="events-filter-modal__ctas">
              <div
                className={`events-filter-modal__ctas--reset-button ${ctaTypes.baseButton} ${ctaTypes.primaryWhite}`}
                onClick={() => {
                  model.resetFilter(navigate);
                  setSelectedFiltersCopy({
                    [EVENT_QUERY_STRINGS.EVENT_PAGE]: '',
                    [EVENT_QUERY_STRINGS.EVENT_TIMELINE]: '',
                    [EVENT_QUERY_STRINGS.EVENT_TYPE]: '',
                  });
                }}
              >
                <span className="events-filter-modal__ctas--label">
                  <DictionaryTerm keyword="cnhi-events-reset-filters" />
                </span>
              </div>
              <div
                className={`button ${ctaTypes.baseButton} ${ctaTypes.primaryRed} events-filter-modal__ctas--apply-button`}
                onClick={() => {
                  setSelectedFiltersCopy(selectedFilters);
                  toggleModal();
                }}
              >
                <span className="events-filter-modal__ctas--label">
                  <DictionaryTerm keyword="cnhi-events-apply-filters" />
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MobileFilter;
